import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../AuthContext';
import { getAttribution, isInAppBrowser } from '../utils/attribution';

const FEATURES = [
  'Multi-channel listing across eBay, Shopify & more',
  'AI-powered pricing from real comp data',
  'Order management & shipping labels in one place',
  'Portfolio tracking & collection valuation',
  'Financial reporting built for card dealers',
];

// Only allow internal relative paths (prevents open-redirect via ?next=//evil.com)
function safeNext() {
  try {
    const n = new URLSearchParams(window.location.search).get('next');
    if (n && n.startsWith('/') && !n.startsWith('//')) return n;
  } catch { /* ignore */ }
  return '/';
}

function Login() {
  const navigate = useNavigate();
  const { login, googleLogin, verifyTwoFactor } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const [twoFaChallenge, setTwoFaChallenge] = useState(null); // set when the server asks for an email code
  const [code, setCode] = useState('');
  // Google OAuth is blocked inside social-app in-app browsers, hide the
  // button there so users fall back to the working email/password form.
  const inApp = isInAppBrowser();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r?.twoFactorRequired) { setTwoFaChallenge(r.challenge); setLoading(false); return; }
      // Honor ?next (invite accept flow), else '/', App.js handles the onboarding gate.
      navigate(safeNext());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (loading) return;
    setLoading(true);
    try {
      setError('');
      // Pass UTM/gclid in case this Google login creates a new account, preserves ad attribution
      const r = await googleLogin(credentialResponse.credential, getAttribution());
      if (r?.twoFactorRequired) { setTwoFaChallenge(r.challenge); setLoading(false); return; }
      // Honor ?next (invite accept flow), else '/', see handleSubmit comment.
      navigate(safeNext());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyTwoFactor(twoFaChallenge, code.trim());
      navigate(safeNext());
    } catch (err) {
      setError(err.response?.data?.error || 'That code is wrong or expired.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (name) => ({
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: `1px solid ${focused === name ? '#1c56cc' : 'rgba(255,255,255,0.1)'}`,
    background: focused === name ? 'rgba(28, 86, 204,0.06)' : 'rgba(255,255,255,0.04)',
    color: 'white', fontSize: 14, boxSizing: 'border-box',
    fontFamily: 'inherit', outline: 'none',
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'DM Sans', -apple-system, sans-serif", background: '#060810' }}>

      {/* LEFT PANEL, hidden on mobile */}
      <div style={{
        width: '45%', minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f1629 0%, #0a0e1a 100%)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', padding: '40px 56px',
      }} className="login-left-panel">
        <div style={{ position: 'absolute', top: -150, left: -150, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(28, 86, 204,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -80, right: -80, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(28, 86, 204,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, zIndex: 1, textDecoration: 'none' }}>
          <div style={{ width: 36, height: 36, background: '#1c56cc', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white', fontFamily: 'monospace' }}>SC</div>
          <span style={{ fontSize: 17, fontWeight: 600, color: 'white', letterSpacing: '-0.3px' }}>SportsCardNetwork</span>
        </Link>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', zIndex: 1 }}>
          <div style={{ fontSize: 38, fontWeight: 700, lineHeight: 1.2, color: 'white', marginBottom: 16, letterSpacing: '-0.7px' }}>
            Scan a card.<br />
            <span style={{ color: '#1c56cc' }}>List it to eBay in one click.</span>
          </div>
          <div style={{ fontSize: 15, color: '#64748b', lineHeight: 1.65, marginBottom: 40 }}>
            AI identifies the card, writes the description, and lists it, while you're still holding it.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {FEATURES.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1c56cc', flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: '#94a3b8' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="auth-right-panel" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(24px, 5vw, 48px)' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'white', marginBottom: 6, letterSpacing: '-0.3px' }}>Welcome back</div>
          <div style={{ fontSize: 14, color: '#64748b', marginBottom: 28 }}>Sign in to your account to continue</div>

          {error && (
            <div style={{ background: 'rgba(220,38,38,0.1)', color: '#f87171', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, border: '1px solid rgba(220,38,38,0.2)' }}>
              {error}
            </div>
          )}

          {twoFaChallenge ? (
            <form onSubmit={handleVerifyCode}>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 16, lineHeight: 1.55 }}>
                We emailed a 6-digit code to <strong style={{ color: '#cbd5e1' }}>{email || 'your email'}</strong>. Enter it below to finish signing in.
              </div>
              <input id="login-2fa" name="code" inputMode="numeric" autoComplete="one-time-code" value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} onFocus={() => setFocused('code')} onBlur={() => setFocused(null)}
                placeholder="123456" required maxLength={6} style={{ ...inputStyle('code'), letterSpacing: 6, textAlign: 'center', fontSize: 18 }} />
              <button type="submit" disabled={loading || code.length < 6} style={{ width: '100%', padding: 12, marginTop: 16, background: '#1c56cc', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: (loading || code.length < 6) ? 0.7 : 1 }}>
                {loading ? 'Verifying...' : 'Verify and sign in'}
              </button>
              <button type="button" onClick={() => { setTwoFaChallenge(null); setCode(''); setError(''); }} style={{ width: '100%', background: 'none', border: 'none', color: '#64748b', fontSize: 12.5, cursor: 'pointer', marginTop: 14 }}>
                Back to sign in
              </button>
            </form>
          ) : (
          <>
          {inApp ? (
            <div style={{ background: 'rgba(28, 86, 204,0.08)', border: '1px solid rgba(28, 86, 204,0.2)', color: '#94a3b8', padding: '11px 14px', borderRadius: 8, marginBottom: 20, fontSize: 12.5, lineHeight: 1.55 }}>
              Sign in with email below. Google sign-in isn't available inside an in-app browser, to use it, open this page in Safari or Chrome (tap the <strong style={{ color: '#cbd5e1' }}>⋯</strong> menu, then "Open in browser").
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-in failed')} size="large" width="340" text="signin_with" theme="filled_black" />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, color: '#374151', fontSize: 12 }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
                <span>or sign in with email</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="login-email" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#94a3b8', marginBottom: 7 }}>Email address</label>
              <input id="login-email" name="email" type="email" autoComplete="email" inputMode="email" autoCapitalize="off" autoCorrect="off" spellCheck={false} value={email} onChange={e => setEmail(e.target.value)} onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} placeholder="you@example.com" required style={inputStyle('email')} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                <label htmlFor="login-password" style={{ fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize: 12, color: '#1c56cc', textDecoration: 'none' }}>Forgot password?</Link>
              </div>
              <input id="login-password" name="password" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} onFocus={() => setFocused('password')} onBlur={() => setFocused(null)} placeholder="••••••••" required style={inputStyle('password')} />
            </div>
            <button type="submit" disabled={loading} style={{ width: '100%', padding: 12, background: '#1c56cc', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1, marginBottom: 20 }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div style={{ textAlign: 'center', fontSize: 13, color: '#64748b' }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: '#1c56cc', textDecoration: 'none', fontWeight: 500 }}>Sign up free</Link>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
